import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { KnowledgeBaseInterface, KnowledgeSource, NamespaceInterface } from '@vendasta/embeddings';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';
import { isTrainingFinished, KnowledgeSourceWithSuggestedFlag } from '../ai-knowledge-util';
import { AiKnowledgeService } from '../services/ai-knowledge.service';
import {
  CAN_SHARE_TOKEN,
  EDITING_ENABLED_TOKEN,
  KNOWLEDGE_GRANTS_ENABLED_TOKEN,
  PARTNER_NAME_RESOLVER_TOKEN,
  SHOW_BUSINESS_PROFILE_SOURCE_TOKEN,
} from '../tokens';
import { KnowledgeBaseTableComponent } from './knowledge-base-table/knowledge-base-table.component';
import { KnowledgeSourceTableComponent } from './knowledge-source-table/knowledge-source-table.component';
import { kbIdentityKey, namespaceInterfaceToTarget, namespacesEqual } from './namespace.util';
import {
  OWNED_SENTINEL,
  TableKnowledgeBase,
  TableKnowledgeSource,
  TableKSKind,
} from './types';

export interface KnowledgePageContext {
  /** i18n key for the page title, rendered through the `translate` pipe (e.g. `'KNOWLEDGE'`). */
  pageTitle: string;
  /** Override for the derived CAN_SHARE_TOKEN (storybook). */
  canShare?: boolean;
}

/**
 * Knowledge management page. A thin container that fetches knowledge bases + sources, derives each
 * row's `managedBy` from its namespace, and hands the rows to the sources / bases table views. The
 * tab switcher uses mat-button-toggle + @if rather than mat-tab-group, whose absolutely-positioned
 * body reports zero height inside glxy-page and breaks scrolling.
 *
 * Pass `kbRows` / `ksRows` to render canned data; omit them to fetch from `AiKnowledgeService`.
 */
@Component({
  selector: 'ai-knowledge-knowledge-management',
  imports: [
    CommonModule,
    GalaxyPageModule,
    MatButtonToggleModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    KnowledgeSourceTableComponent,
    KnowledgeBaseTableComponent,
  ],
  templateUrl: './knowledge-management.component.html',
  styleUrls: ['./knowledge-management.component.scss'],
})
export class KnowledgeManagementComponent implements OnInit {
  @Input({ required: true }) context!: KnowledgePageContext;
  /** When set, skips the service fetch (storybook/tests). */
  @Input() set kbRows(value: TableKnowledgeBase[] | undefined) {
    if (value !== undefined) {
      this.kbRowsData.set(value);
      this.skipServiceFetch.set(true);
    }
  }
  /** When set, skips the service fetch (storybook/tests). */
  @Input() set ksRows(value: TableKnowledgeSource[] | undefined) {
    if (value !== undefined) {
      this.ksRowsData.set(value);
      this.skipServiceFetch.set(true);
    }
  }

  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly translate = inject(TranslateService);
  private readonly canShare$ = inject(CAN_SHARE_TOKEN);
  private readonly editingEnabled$ = inject(EDITING_ENABLED_TOKEN);
  private readonly grantsEnabled$ = inject(KNOWLEDGE_GRANTS_ENABLED_TOKEN);
  private readonly partnerNameResolver = inject(PARTNER_NAME_RESOLVER_TOKEN);
  private readonly showBusinessProfileSource$ = inject(SHOW_BUSINESS_PROFILE_SOURCE_TOKEN);
  private readonly datePipe = new DatePipe('en-US');

  readonly kbRowsData = signal<TableKnowledgeBase[]>([]);
  readonly ksRowsData = signal<TableKnowledgeSource[]>([]);
  /** Raw sources by ID, so the sources table's Edit/Refresh/Delete have the full config. Empty in @Input mode. */
  readonly rawSourcesById = signal<Map<string, KnowledgeSource>>(new Map());
  readonly isLoading = signal(false);
  readonly activeTab = signal<'sources' | 'bases'>('sources');
  /** Gates the auto-fetch when rows are passed in. */
  private readonly skipServiceFetch = signal(false);

  // Latest token values, as signals for the template.
  readonly canShareToken = signal(true);
  readonly editingEnabled = signal(true);
  readonly grantsEnabledToken = signal(false);

  /** Share UI requires the grants flag AND canShare; the storybook `context.canShare` override bypasses the flag. */
  readonly canShareEffective = computed(() => {
    if (this.context?.canShare !== undefined) return this.context.canShare;
    return this.grantsEnabledToken() && this.canShareToken();
  });

  /** Exposed for the base table's `persist` input (false in storybook/@Input mode). */
  readonly persistChanges = computed(() => !this.skipServiceFetch());

  async ngOnInit(): Promise<void> {
    this.canShareToken.set((await firstValueFrom(this.canShare$.pipe()).catch(() => true)) ?? true);
    this.editingEnabled.set((await firstValueFrom(this.editingEnabled$.pipe()).catch(() => true)) ?? true);
    const grantsOn = (await firstValueFrom(this.grantsEnabled$.pipe()).catch(() => false)) ?? false;
    this.grantsEnabledToken.set(grantsOn);

    if (this.skipServiceFetch()) return;
    if (!grantsOn) return; // when flag-off, the consumer renders the legacy page instead

    await this.loadFromServices();
  }

  /** A source-table action mutated server state — refetch unless we're in canned-data mode. */
  async onSourcesChanged(): Promise<void> {
    if (this.skipServiceFetch()) return;
    await this.loadFromServices();
  }

  /** Fetches KBs and sources and rebuilds the row sets. */
  private async loadFromServices(): Promise<void> {
    this.isLoading.set(true);
    try {
      const kbs = await this.knowledgeService.listKnowledgeBases(true);
      const sources = await this.knowledgeService.listAllKnowledgeSources();

      // Business Profile is virtual; synthesize one only when the BE hasn't materialized it yet, so the real source's id/training state wins when present.
      const showBP = (await firstValueFrom(this.showBusinessProfileSource$).catch(() => false)) ?? false;
      if (showBP) {
        const bpId = this.knowledgeService.buildBusinessProfileKnowledgeSourceId();
        const hasBP = sources.some((s) => s.id === bpId || !!s.config?.businessProfileConfig);
        if (!hasBP) {
          const bp = this.knowledgeService.buildBusinessProfileKnowledgeSource(true);
          if (bp) sources.unshift(bp);
        }
      }

      const callerNamespace = this.knowledgeService.buildNamespaceInterface();
      const rawById = new Map<string, KnowledgeSource>();
      for (const ks of sources) {
        if (ks.id) rawById.set(ks.id, ks);
      }
      const kbRows: TableKnowledgeBase[] = await Promise.all(
        kbs.map((kb) => this.toKbRow(kb, callerNamespace)),
      );
      const ksRows: TableKnowledgeSource[] = await Promise.all(
        sources.map((ks) => this.toKsRow(ks, kbs, callerNamespace)),
      );
      this.rawSourcesById.set(rawById);
      this.kbRowsData.set(kbRows);
      this.ksRowsData.set(ksRows);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async toKbRow(
    kb: KnowledgeBaseInterface,
    callerNs: NamespaceInterface,
  ): Promise<TableKnowledgeBase> {
    const sourceIds = (kb.knowledgeSourceKeys ?? [])
      .map((key) => key.knowledgeSourceId)
      .filter((id): id is string => !!id);
    return {
      id: kb.id ?? '',
      kbKey: kbIdentityKey(kb.namespace, kb.id),
      name: kb.name ?? '',
      description: kb.description ?? '',
      sourceCount: sourceIds.length,
      sourceIds,
      managedBy: await this.resolveManagedBy(kb.namespace, callerNs),
      lastModified: new Date(),
    };
  }

  private async toKsRow(
    ks: KnowledgeSource,
    kbs: KnowledgeBaseInterface[],
    callerNs: NamespaceInterface,
  ): Promise<TableKnowledgeSource> {
    const parentKb = kbs.find((kb) =>
      (kb.knowledgeSourceKeys ?? []).some((key) => key.knowledgeSourceId === ks.id),
    );
    const websiteCfg = ks.config?.websiteScrapeConfig;
    const autoRefreshTime = websiteCfg?.options?.automaticRefreshTime;
    const nextRefresh = websiteCfg?.refreshScheduledFor;
    let autoRefreshTooltip: string | undefined;
    if (autoRefreshTime) {
      const date =
        this.datePipe.transform(nextRefresh, 'mediumDate') ??
        (await firstValueFrom(this.translate.get('KNOWLEDGE_SHARING.SOURCE_TABLE.AUTO_REFRESH_SCHEDULED')));
      autoRefreshTooltip = await firstValueFrom(
        this.translate.get('KNOWLEDGE_SHARING.SOURCE_TABLE.AUTO_REFRESH_TOOLTIP', { date }),
      );
    }
    return {
      id: ks.id ?? '',
      kind: this.ksKindFromConfig(ks),
      name: ks.name ?? '',
      subtitle: this.knowledgeService.getKnowledgePreview(ks),
      description: ks.description ?? '',
      // KB membership lives on the KB row (sourceIds); parentKb here only attributes ownership for managedBy.
      managedBy: await this.resolveManagedBy(parentKb?.namespace, callerNs),
      lastModified: new Date(),
      isSuggested: (ks as KnowledgeSourceWithSuggestedFlag).isSuggested === true,
      isTrainingInProgress: !isTrainingFinished(ks.training),
      hasAutoRefresh: Boolean(autoRefreshTime),
      autoRefreshTooltip,
      lastScrapedText: websiteCfg ? this.knowledgeService.getLastScraped(ks) : undefined,
    };
  }

  private ksKindFromConfig(ks: KnowledgeSource): TableKSKind {
    if (ks.config?.websiteScrapeConfig) return 'url';
    if (ks.config?.fileConfig) {
      const mt = ks.config.fileConfig.mimetype ?? '';
      return mt === 'application/pdf' ? 'pdf' : 'file';
    }
    if (ks.config?.customDataConfig || ks.config?.businessProfileConfig) return 'custom';
    return 'file';
  }

  /** "Owned" when the KB is the caller's, else the namespace's display name via the injected resolver. */
  private async resolveManagedBy(
    kbNs: NamespaceInterface | undefined,
    callerNs: NamespaceInterface,
  ): Promise<string> {
    if (!kbNs || namespacesEqual(kbNs, callerNs)) return OWNED_SENTINEL;
    const target = namespaceInterfaceToTarget(kbNs);
    if (!target) return OWNED_SENTINEL;
    try {
      return (await firstValueFrom(this.partnerNameResolver(target).pipe())) ?? target.id;
    } catch {
      return target.id;
    }
  }
}
